import { getTimezone } from "@/lib/timezone";

import type {
  MeasurementRecordCreate,
  MeasurementValueSummary,
  MeasurementValueUpdate,
  PatientMeasurementsResponse,
  PrefillResponse,
} from "@/types/measurement";

import { requestData } from "@/api/api-config";

// Calculated values pair their inputs by day, on the clinic's clock.
const clinicDay = () => ({ tz: getTimezone() });

export async function recordMeasurements(
  data: MeasurementRecordCreate,
): Promise<MeasurementValueSummary[] | null> {
  return await requestData<MeasurementValueSummary[]>(
    "user/patient/measurement",
    "post",
    { json: data, searchParams: clinicDay() },
  );
}

export async function getPatientMeasurements(
  patientId: string,
): Promise<PatientMeasurementsResponse | null> {
  return await requestData<PatientMeasurementsResponse>(
    `user/patient/measurement/${patientId}`,
    "get",
    { searchParams: clinicDay() },
  );
}

export async function getFieldTrend(
  patientId: string,
  fieldId: string,
): Promise<MeasurementValueSummary[] | null> {
  return await requestData<MeasurementValueSummary[]>(
    `user/patient/measurement/${patientId}/field/${fieldId}/trend`,
    "get",
    { searchParams: clinicDay() },
  );
}

export async function updateMeasurementValue(
  patientId: string,
  valueId: string,
  data: MeasurementValueUpdate,
): Promise<MeasurementValueSummary | null> {
  return await requestData<MeasurementValueSummary>(
    `user/patient/measurement/${patientId}/value/${valueId}`,
    "put",
    { json: data, searchParams: clinicDay() },
  );
}

export async function archiveMeasurementValue(
  patientId: string,
  valueId: string,
): Promise<void> {
  await requestData(
    `user/patient/measurement/${patientId}/value/${valueId}`,
    "delete",
  );
}

export async function getGroupPrefill(
  patientId: string,
  groupId: string,
  start: string,
  end: string,
): Promise<PrefillResponse | null> {
  const params = new URLSearchParams({ start, end });
  return await requestData<PrefillResponse>(
    `user/patient/measurement/${patientId}/group/${groupId}/prefill?${params.toString()}`,
    "get",
  );
}

export async function getFieldsPrefill(
  patientId: string,
  fieldIds: string[],
  start: string,
  end: string,
): Promise<PrefillResponse | null> {
  const params = new URLSearchParams({ start, end });
  for (const id of fieldIds) {
    params.append("field_ids", id);
  }
  return await requestData<PrefillResponse>(
    `user/patient/measurement/${patientId}/fields/prefill?${params.toString()}`,
    "get",
  );
}
